// views/Dashboard/PainelExecutivo/areas/visaoGeral/blocos.tsx
//
// Um bloco por área na Visão geral. Cada um usa o MESMO hook e as MESMAS
// funções puras da aba correspondente — se o resumo tivesse conta própria, o
// número da capa divergiria do número de dentro e ninguém saberia qual vale.
import React, { useMemo } from 'react';
import type { FiltrosDashboard } from '../../types';
import BlocoResumo from './BlocoResumo';
import type { Metrica } from './BlocoResumo';
import { calcularJanelaPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import type { PeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { filtrarFaturasPorPeriodo } from '../../../../../src/lib/dashboard/filtrarFaturasPorPeriodo';
import { calcularKpis } from '../../../../../src/lib/financeiro/calcularKpis';
import { janelaParaPeriodoComercial } from '../../../../../src/lib/dashboard/janelaParaPeriodoComercial';
import { filtrarPorDepartamento } from '../../../../../src/lib/dashboard/filtrarNegociacoesPorDepartamento';
import { computeEventFunnel } from '../../../../CRM/Analytics/eventFunnel';
import { aplicarContagemFunil } from '../../../../../src/lib/dashboard/funilJornadaNoPeriodo';
import { useFinanceiroDashboardData } from '../../sections/financeiro/useFinanceiroDashboardData';
import { useComercialDashboardData } from '../../sections/comercial/useComercialDashboardData';
import { useGeradorasDashboardData } from '../../sections/geradoras/useGeradorasDashboardData';
import { useMarketingDashboardData } from '../../sections/marketing/useMarketingDashboardData';

interface Props {
  filtros: FiltrosDashboard;
  /** Período anterior equivalente, para a variação. `null` em "Todo o histórico". */
  anterior: PeriodoDashboard | null;
  rotuloAnterior: string | null;
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const fmtNum = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const fmtKwh = (v: number) => `${fmtNum(v)} kWh`;
const fmtPct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export const BlocoFinanceiro: React.FC<Props> = ({ filtros, anterior, rotuloAnterior }) => {
  const { dados, loading, erro } = useFinanceiroDashboardData();
  const hoje = useMemo(() => new Date(), []);

  const kpis = useMemo(
    () => calcularKpis(filtrarFaturasPorPeriodo(dados, filtros.periodo, hoje, filtros.baseDataFinanceiro), hoje),
    [dados, filtros.periodo, filtros.baseDataFinanceiro, hoje],
  );
  const kpisAnterior = useMemo(
    () => (anterior ? calcularKpis(filtrarFaturasPorPeriodo(dados, anterior, hoje, filtros.baseDataFinanceiro), hoje) : null),
    [dados, anterior, filtros.baseDataFinanceiro, hoje],
  );

  const metricas: Metrica[] = [
    { titulo: 'Emitido', valor: fmtBRL(kpis.emitido), bruto: kpis.emitido, anterior: kpisAnterior?.emitido, detalhe: `${fmtNum(kpis.qtdeBoletos)} boletos` },
    { titulo: 'Recebido', valor: fmtBRL(kpis.recebido), bruto: kpis.recebido, anterior: kpisAnterior?.recebido, detalhe: `${fmtPct(kpis.recebidoPctEmitido)} do emitido` },
    { titulo: 'Vencido', valor: fmtBRL(kpis.vencido), bruto: kpis.vencido, anterior: kpisAnterior?.vencido, crescerEhBom: false, detalhe: `${fmtNum(kpis.vencidoQtdeBoletos)} boletos` },
    { titulo: 'Inadimplência', valor: fmtPct(kpis.taxaInadimplencia), bruto: kpis.taxaInadimplencia, anterior: kpisAnterior?.taxaInadimplencia, crescerEhBom: false, detalhe: 'vencido ÷ emitido' },
  ];

  return (
    <BlocoResumo
      titulo="Financeiro"
      para="financeiro"
      metricas={metricas}
      loading={loading && dados.length === 0}
      erro={erro}
      rotuloAnterior={rotuloAnterior}
    />
  );
};

export const BlocoComercial: React.FC<Props> = ({ filtros, anterior, rotuloAnterior }) => {
  const { dados, profilesById, loading, erro } = useComercialDashboardData();
  const hoje = useMemo(() => new Date(), []);

  const filtradas = useMemo(
    () => filtrarPorDepartamento(dados, profilesById, filtros.departamentos),
    [dados, profilesById, filtros.departamentos],
  );
  // Segue o seletor "Contar" da aba Comercial, como o bloco Financeiro segue a
  // base de data: a capa não pode dizer um número e a aba, outro.
  const contagem = filtros.contagemComercial;
  const etapas = (periodo: PeriodoDashboard) => {
    const { periodo: p, customRange } = janelaParaPeriodoComercial(calcularJanelaPeriodoDashboard(periodo, hoje), hoje);
    return aplicarContagemFunil(computeEventFunnel(filtradas, p, customRange), contagem, p, customRange);
  };
  const stages = useMemo(() => etapas(filtros.periodo), [filtradas, filtros.periodo, hoje, contagem]);
  const stagesAnterior = useMemo(() => (anterior ? etapas(anterior) : null), [filtradas, anterior, hoje, contagem]);

  const de = (lista: typeof stages | null, chave: string) => lista?.find((s) => s.key === chave);
  const prospeccao = de(stages, 'prospeccao');
  const assinado = de(stages, 'termo_assinado');
  const aprovado = de(stages, 'termo_aprovado');
  // Conversão da ponta a ponta: o que entrou virou contrato aprovado?
  const conversao = prospeccao && prospeccao.leads > 0 ? (aprovado?.leads ?? 0) / prospeccao.leads : 0;
  const prospAnterior = de(stagesAnterior, 'prospeccao');
  const conversaoAnterior = prospAnterior && prospAnterior.leads > 0
    ? (de(stagesAnterior, 'termo_aprovado')?.leads ?? 0) / prospAnterior.leads
    : null;

  const metricas: Metrica[] = [
    { titulo: 'Prospecções', valor: fmtNum(prospeccao?.leads ?? 0), bruto: prospeccao?.leads ?? 0, anterior: prospAnterior?.leads },
    { titulo: 'Termos assinados', valor: fmtNum(assinado?.leads ?? 0), bruto: assinado?.leads ?? 0, anterior: de(stagesAnterior, 'termo_assinado')?.leads, detalhe: fmtKwh(assinado?.kwh ?? 0) },
    { titulo: 'Termos aprovados', valor: fmtNum(aprovado?.leads ?? 0), bruto: aprovado?.leads ?? 0, anterior: de(stagesAnterior, 'termo_aprovado')?.leads, detalhe: fmtKwh(aprovado?.kwh ?? 0) },
    { titulo: 'Conversão', valor: fmtPct(conversao), bruto: conversao, anterior: conversaoAnterior, detalhe: contagem === 'jornada' ? 'prospecção → aprovado, tudo no período' : 'prospecção → aprovado' },
  ];

  return (
    <BlocoResumo
      titulo="Comercial"
      para="comercial"
      metricas={metricas}
      loading={loading && dados.length === 0}
      erro={erro}
      rotuloAnterior={rotuloAnterior}
    />
  );
};

export const BlocoGeradoras: React.FC<Props> = ({ filtros, anterior, rotuloAnterior }) => {
  const { pontos, energiaInjetadaKwh, saldoAcumuladoKwh, loading, erro } = useGeradorasDashboardData(filtros.periodo);
  const { pontos: pontosAnterior, energiaInjetadaKwh: injetadaAnterior } = useGeradorasDashboardData(anterior);

  const soma = (lista: typeof pontos, campo: 'energiaGeradaKwh' | 'energiaEsperadaKwh') =>
    lista.reduce((s, p) => s + p[campo], 0);
  const gerado = soma(pontos, 'energiaGeradaKwh');
  const esperado = soma(pontos, 'energiaEsperadaKwh');
  const aderencia = esperado > 0 ? gerado / esperado : 0;
  const esperadoAnterior = soma(pontosAnterior, 'energiaEsperadaKwh');

  const metricas: Metrica[] = [
    { titulo: 'Energia gerada', valor: fmtKwh(gerado), bruto: gerado, anterior: anterior ? soma(pontosAnterior, 'energiaGeradaKwh') : null },
    { titulo: 'Aderência ao esperado', valor: fmtPct(aderencia), bruto: aderencia, anterior: anterior && esperadoAnterior > 0 ? soma(pontosAnterior, 'energiaGeradaKwh') / esperadoAnterior : null, detalhe: `esperado ${fmtKwh(esperado)}` },
    { titulo: 'Energia injetada', valor: fmtKwh(energiaInjetadaKwh), bruto: energiaInjetadaKwh, anterior: anterior ? injetadaAnterior : null },
    { titulo: 'Saldo das usinas', valor: fmtKwh(saldoAcumuladoKwh), detalhe: 'foto de hoje, não do período' },
  ];

  return (
    <BlocoResumo
      titulo="Geradoras"
      para="geradoras"
      metricas={metricas}
      loading={loading && pontos.length === 0}
      erro={erro}
      rotuloAnterior={rotuloAnterior}
    />
  );
};

export const BlocoMarketing: React.FC<Props> = ({ filtros, anterior, rotuloAnterior }) => {
  const { kpis, byDay, loading, erro } = useMarketingDashboardData(filtros.periodo);
  const { kpis: kpisAnterior } = useMarketingDashboardData(anterior);

  const metricas: Metrica[] = [
    { titulo: 'Investimento', valor: fmtBRL(kpis.totalSpend), bruto: kpis.totalSpend, anterior: anterior ? kpisAnterior.totalSpend : null, crescerEhBom: false },
    { titulo: 'Resultados', valor: fmtNum(kpis.totalResults), bruto: kpis.totalResults, anterior: anterior ? kpisAnterior.totalResults : null },
    { titulo: 'Custo por resultado', valor: fmtBRL(kpis.avgCostPerResult), bruto: kpis.avgCostPerResult, anterior: anterior ? kpisAnterior.avgCostPerResult : null, crescerEhBom: false },
    { titulo: 'Leads / cadastros', valor: fmtNum(kpis.totalLeads), bruto: kpis.totalLeads, anterior: anterior ? kpisAnterior.totalLeads : null },
  ];

  return (
    <BlocoResumo
      titulo="Marketing"
      para="marketing"
      metricas={metricas}
      loading={loading && byDay.length === 0}
      erro={erro}
      rotuloAnterior={rotuloAnterior}
    />
  );
};
